import { Modal, Row, Upload } from "antd"
import cx from "clsx"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import Lottie from "react-lottie"
import theme from "utils/themeVars"
import { Group, Text, notify } from ".."
import animationData from "../../../animation/uploadFileAnimation.json"

import { downloadPDF, fileToBase64, isNotImageCheck, isPDF, isValidFileType, openBase64 } from "../../../utils"
import { IconText } from "../../custom"
import styles from "./index.module.less"

const allowedFileTypes = ["image/jpeg", "image/jpg", "image/png, application/pdf"]
const defaultOptions = { loop: true, autoplay: true, animationData: animationData }

export default function ImageUpload(props) {
  const { t } = useTranslation()
  const {
    className,
    fileSize = 1,
    minDimensions = [400, 400],
    maxDimensions = [1024, 1024],
    withDimensionLimit = true,
    allowedFiles,
    setFiles = () => {},
    internalState,
    files,
    showOnly,
    limit = 1,
    mode,
    outerStyle,
    showButton = true,
    allowPDFDownload = true,
    uploadDescription = t(
      "Must be in JPG, PNG, or PDF file format up to {{fileSize}} MB. Upload all the evidence screenshots, chat conversations, or emails that help in your claim.",
      { fileSize: fileSize }
    ),
    uploadTitle = t("Click or drag file to this area to upload"),
    onChange,
    emptyText,
    liftState = () => {},
    LottieSize,
    isAspectRatioRequired,
    aspectRatioProps = {},
    onRemove = () => {},
    ...rest
  } = props

  const [file, setFile] = useState({ previewTitle: "", previewImage: "", previewOpen: false })
  const [internalFileState, setInternalFileState] = useState(files)

  useEffect(() => {
    if (internalState) setInternalFileState(files)
  }, [files, internalState])

  const handleCancel = () =>
    setFile(prev => {
      return { ...prev, previewOpen: false }
    })

  const isValidUrl = url => {
    try {
      new URL(url)
      return true
    } catch (err) {
      return false
    }
  }

  const handlePreview = async file => {
    if (!file.url && !file.preview) {
      file.preview = await fileToBase64(file.originFileObj)
    }

    if (isPDF(file?.fileName) && file?.localPath) {
      downloadPDF(file?.localPath?.split(",")?.[1], file?.fileName)
    }

    if (isValidUrl(file?.url || file?.preview)) {
      if (isPDF(file?.url || file?.preview)) {
        allowPDFDownload && window.open(file?.url || file?.preview)
      } else if (isNotImageCheck(file)) {
        openBase64(file?.url || file?.preview)
      }
    }

    setFile(prev => {
      return {
        ...prev,
        name: file?.title || file?.name || file?.fileName || file?.originFileObj?.type,
        previewImage: file.url || file.preview,
        previewTitle:
          file.title || file?.name || file?.originFileObj?.name || file?.url?.substring(file.url.lastIndexOf("/") + 1),
        previewOpen: true,
      }
    })
  }

  const customUpload = file => {
    setTimeout(() => {
      file.onSuccess("ok")
    }, 0)
  }
  const processFile = (file, resolve) => {
    fileToBase64(file).then(base64File => {
      setFiles({ name: file.name, url: base64File, uid: "0" })
      setFile(prev => {
        return {
          ...prev,
          containFile: true,
        }
      })
    })
    resolve(true)
  }
  const beforeUpload = file => {
    return new Promise((resolve, reject) => {
      const isDuplicate = (internalFileState || files)?.some(
        existingFile => existingFile?.name === file?.name && existingFile?.size === file?.size
      )
      if (isDuplicate) {
        notify.error(t("Cannot add duplicate file"))
        return Upload.LIST_IGNORE
      }

      const isLt5M = file.size / 1024 / 1024 <= fileSize

      if (!isLt5M) {
        notify.error(`${t("File size can not be greater than")}${fileSize}MB`)
        return Upload.LIST_IGNORE
      }
      if (rest?.targets && !isValidFileType(allowedFiles, file)) {
        notify.error(t("Invalid file format. Please upload an Excel file."))
        return
      }
      if (isNotImageCheck(file)) {
        rest?.targets || rest?.platformKey === "bayut_ksa"
          ? processFile(file, resolve)
          : notify.error(t("Invalid file format. Please upload an image in PNG, JPEG or JPG format"))
      } else if (isAspectRatioRequired) {
        var reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = e => {
          var image = new Image()
          image.src = e.target.result
          image.onload = function () {
            let aspectRatio = (this.naturalWidth / this.naturalHeight).toFixed(2)
            if (!aspectRatioProps.list.some(ratio => ratio == aspectRatio)) {
              notify.error(t(`File should be in ${aspectRatioProps?.message} aspect ratio`))
              return Upload.LIST_IGNORE
            } else {
              processFile(file, resolve)
            }
          }
        }
      } else {
        var reader = new FileReader()
        reader.readAsDataURL(file)

        reader.onload = e => {
          var image = new Image()
          image.src = e.target.result
          image.onload = function () {
            const height = this.height
            const width = this.width
            if (!withDimensionLimit) {
              processFile(file, resolve)
            } else if (
              width <= maxDimensions[0] &&
              width >= minDimensions[0] &&
              height <= maxDimensions[1] &&
              height >= minDimensions[1]
            ) {
              processFile(file, resolve)
            } else {
              notify.error(`Image size is not within the range of ${maxDimensions[0]}px and ${minDimensions[0]}px`)
              return Upload.LIST_IGNORE
            }
          }
        }
      }
    })
  }

  const renderUploader = () => (
    <Group
      template="auto 1fr"
      gap={uploadTitle || uploadDescription ? "24px" : "0px"}
      className="p-16"
      style={{ "align-items": "center" }}
    >
      <Lottie options={defaultOptions} height={LottieSize ? LottieSize : 56} width={LottieSize ? LottieSize : 56} />
      {uploadTitle || uploadDescription ? (
        <Row className="text-left" style={{ gap: 8 }}>
          {uploadTitle && (
            <Text color={theme["primary-color"]} block>
              <span className="semiBold">{uploadTitle}</span>
            </Text>
          )}
          {uploadDescription && (
            <IconText
              icon="BsInfo"
              title={uploadDescription}
              iconProps={{
                style: { color: "#707070" },
                hasBackground: true,
                size: "1.5em",
                iconContainerStyle: { marginTop: 4, padding: 0, "--icon-bg-color": "#E6E6E6" },
              }}
              style={{ alignItems: "flex-start", "--typography-color": "#9D9D9D", fontSize: 12, gap: 8 }}
              titleProps={{ style: { fontSize: 12 } }}
            />
          )}
        </Row>
      ) : null}
    </Group>
  )

  return (
    <>
      <div className={cx(styles.uploadOuter, className)} style={outerStyle}>
        <Upload
          maxCount={limit}
          listType={rest?.noCard ? "picture-card" : "picture"}
          fileList={internalState ? internalFileState : files}
          onPreview={handlePreview}
          previewFile={file => fileToBase64(file)}
          showUploadList={{ showRemoveIcon: !showOnly, showPreviewIcon: allowPDFDownload }}
          customRequest={customUpload}
          beforeUpload={beforeUpload}
          className={rest?.noCard && cx(styles.upload)}
          onRemove={file => {
            if (mode !== "multiple") {
              setFiles(null)
              setFile(prev => ({ ...prev, containFile: false }))
            }
            if (liftState) {
              liftState(files.filter(item => (item?.id ? item?.id !== file?.id : item?.uid !== file?.uid)))
            }
            if (onRemove) {
              onRemove(file)
            }
          }}
          accept={allowedFiles || allowedFileTypes.join(",")}
          onChange={onChange}
          multiple={mode === "multiple"}
          {...rest}
        >
          {mode === "multiple" && limit !== files?.length
            ? renderUploader()
            : emptyText || (files?.length && (files || file?.containFile))
            ? null
            : renderUploader()}
        </Upload>
        {emptyText && !files?.length ? <Text>{emptyText}</Text> : null}
        {file.previewImage && !isNotImageCheck(file) && (
          <Modal open={file.previewOpen} title={file.previewTitle} footer={null} onCancel={handleCancel}>
            <img
              alt="example"
              style={{
                width: "100%",
              }}
              src={file.previewImage}
            />
          </Modal>
        )}
      </div>
    </>
  )
}
